/*
 * Gift-with-purchase: everything that reacts to the GWP discount code lives in this
 * file, not just the cart claim module below it - see the PDP kit gift preview at
 * the bottom, which is the same mechanism applied to a paid upsell card instead of
 * the cart claim picker.
 *
 * Claim module: reveals itself only when its own GWP discount code is on the cart.
 * Klaviyo hands that code out at signup, so the code IS the eligibility signal —
 * there is no separate cohort read and no test-group logic here.
 *
 * More than one of these can be in the DOM at once, one per configured gift slot (see
 * snippets/gift-claim.liquid). At most one ever reveals: an element stays hidden if any
 * code listed in data-higher-priority-codes is also on the cart. Each element reaches that
 * verdict alone, from the cart payload plus its own attributes, so there is no coordination
 * between them and no dependence on which upgrades first.
 *
 * The code is read from the cart itself (/cart.js reports applied discount codes and
 * whether each currently applies), not from a flag we set ourselves, so it cannot be
 * forged by editing storage.
 *
 * Fails closed: an unreadable cart, an unexpected shape, or no matching code all mean
 * the module stays hidden. A missing gift is recoverable; a wrongly-given one is not.
 *
 * State lives outside this element — the cart sections are re-rendered on every cart
 * change, which destroys and recreates this DOM. The claimed variant is a cart
 * attribute; a rejection is recorded in sessionStorage.
 *
 * Rejection is deliberately NOT blocking: a customer who removes the gift sees the
 * picker again and can claim it back. The flag only records that it happened.
 *
 * Claims go through /cart/update.js rather than /cart/add.js. That is deliberate: an
 * app on this store wraps window.fetch and resolves successful /cart/add.js responses
 * to undefined. /cart/update.js is unaffected. See the project log.
 */

/*
 * Shared, memoized read of the cart's discount codes and lines - both the claim module
 * and the PDP kit preview need the same answer, so a page carrying both fires one
 * /cart.js request, not one per reader. Memoized only while in flight: a second caller
 * during the same request joins it, but the cart is read fresh on every new check, so a
 * code applied or removed mid-session is picked up without a page reload.
 */
const gwpDiscountCodes = (() => {
  let pending = null;

  const fetchCart = () => {
    if (!pending) {
      pending = fetch(`${routes.cart_url}.js`, { cache: 'no-store' })
        .then((response) => (response && response.ok ? response.json() : null))
        .then((cart) => ({
          codes: Array.isArray(cart?.discount_codes) ? cart.discount_codes : [],
          items: Array.isArray(cart?.items) ? cart.items : [],
        }))
        .catch(() => ({ codes: [], items: [] }))
        .finally(() => {
          pending = null;
        });
    }
    return pending;
  };

  return {
    /**
     * Whether the cart currently carries `code`. Bare-string entries are accepted
     * alongside `{ code, applicable }` objects, since the shape has varied.
     * `applicable` is deliberately not read here - see GiftClaim.revealIfEligible.
     */
    has(code) {
      if (!code) return Promise.resolve(false);
      const wanted = code.toUpperCase();
      return fetchCart().then(({ codes }) =>
        codes.some((entry) => {
          const value = typeof entry === 'string' ? entry : entry?.code;
          return typeof value === 'string' && value.toUpperCase() === wanted;
        })
      );
    },

    /** Whether any cart line is of product `productId`, whatever its price. */
    hasProduct(productId) {
      if (!productId) return Promise.resolve(false);
      const wanted = String(productId);
      return fetchCart().then(({ items }) => items.some((item) => String(item.product_id) === wanted));
    },
  };
})();

if (!customElements.get('gift-claim')) {
  const REJECTED_STORAGE_KEY = 'gwp-gift-rejected';

  class GiftClaim extends HTMLElement {
    connectedCallback() {
      this.swatches = this.querySelectorAll('.gift-claim__swatch');
      this.errorEl = this.querySelector('.gift-claim__error');

      this.onSwatchClick = this.onSwatchClick.bind(this);
      this.swatches.forEach((swatch) => swatch.addEventListener('click', this.onSwatchClick));

      // pubsub.js loads deferred in <head>, so this is normally defined — but the
      // module must not break the cart if it ever isn't.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.checkForRemoval());
      }

      this.revealIfEligible();
    }

    disconnectedCallback() {
      this.swatches?.forEach((swatch) => swatch.removeEventListener('click', this.onSwatchClick));
      this.cartUpdateUnsubscriber?.();
    }

    /* Eligibility ------------------------------------------------------------- */

    /**
     * Reveals the module if its own code is on the cart and no higher-priority code is.
     *
     * Presence only — deliberately NOT `applicable`. For a Buy X Get Y, applicability
     * depends on there being a Y in the cart to discount, and putting the Y there is
     * this component's entire job. Gating on it is circular: no module, so no gift, so
     * never applicable.
     *
     * `applicable` is also ambiguous. Shopify reports false both for "nothing to
     * discount yet" and for "this customer has already used it", and the cart payload
     * does not distinguish them, so it cannot safely gate anything.
     *
     * Verified against the cart on every render rather than cached, so a code removed
     * mid-session stops qualifying immediately.
     */
    async revealIfEligible() {
      if (!this.discountCode) return;

      // One /cart.js between all of them - gwpDiscountCodes joins calls made while
      // a request is in flight.
      const [hasOwnCode, ...hasHigherCodes] = await Promise.all(
        [this.discountCode, ...this.higherPriorityCodes].map((code) => this.hasDiscountApplied(code))
      );
      if (!hasOwnCode || hasHigherCodes.some(Boolean)) return;

      this.hidden = false;
    }

    /**
     * Whether the cart currently carries `code`.
     *
     * cache: 'no-store' (inside gwpDiscountCodes) is required, not defensive. A
     * cached /cart.js can predate the discount being applied or the cart being
     * filled, and a stale empty response reads as "not eligible" — the module then
     * only appears after a hard refresh.
     */
    async hasDiscountApplied(code) {
      try {
        return await gwpDiscountCodes.has(code);
      } catch (error) {
        return false;
      }
    }

    get discountCode() {
      return this.dataset.discountCode || '';
    }

    /** Codes whose slots outrank this one. Blank for the first slot. */
    get higherPriorityCodes() {
      return (this.dataset.higherPriorityCodes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    /* Claiming ---------------------------------------------------------------- */

    onSwatchClick(event) {
      const swatch = event.currentTarget;
      if (swatch.dataset.available !== 'true' || this.isClaiming) return;

      const variantId = swatch.dataset.variantId;
      if (!variantId) return;

      this.claim(variantId, swatch);
    }

    async claim(variantId, swatch) {
      this.isClaiming = true;
      this.setError(false);
      swatch.classList.add('is-loading');

      try {
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            updates: { [variantId]: 1 },
            attributes: { _gwp_claim: String(variantId) },
            sections: this.sectionsToRender(),
            sections_url: window.location.pathname,
          }),
        });

        // A wrapped fetch can resolve to undefined — treat it as a failure rather
        // than dereferencing it, so the customer sees a retry instead of a dead UI.
        if (!response || !response.ok) throw new Error('Cart update failed');

        const data = await response.json();
        this.renderSections(data);
      } catch (error) {
        swatch.classList.remove('is-loading');
        this.isClaiming = false;
        this.setError(true);
      }
    }

    sectionsToRender() {
      const sections = ['cart-icon-bubble'];
      if (document.getElementById('CartDrawer')) sections.push('cart-drawer');

      const cartItems = document.getElementById('main-cart-items');
      if (cartItems?.dataset.id) sections.push(cartItems.dataset.id);

      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter?.dataset.id) sections.push(cartFooter.dataset.id);

      return Array.from(new Set(sections));
    }

    /**
     * Swaps re-rendered cart HTML in. This element is inside those containers, so it
     * is destroyed here — connectedCallback runs again on the replacement.
     */
    renderSections(data) {
      if (!data?.sections) return;

      const drawer = document.getElementById('CartDrawer');
      if (drawer && data.sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const replacement = parsed.getElementById('CartDrawer');
        if (replacement) drawer.innerHTML = replacement.innerHTML;
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const replacement = parsed.querySelector('#cart-icon-bubble .shopify-section, #cart-icon-bubble');
        if (replacement) bubble.innerHTML = replacement.innerHTML;
      }

      [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents-totals' },
      ].forEach(({ id, selector }) => {
        const scope = document.getElementById(id);
        const sectionId = scope?.dataset.id;
        if (!scope || !sectionId || !data.sections[sectionId]) return;

        const parsed = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
        const replacement = parsed.querySelector(selector);
        const container = scope.querySelector(selector);
        if (replacement && container) container.innerHTML = replacement.innerHTML;
      });
    }

    /* Removal ----------------------------------------------------------------- */

    /**
     * Records that a claimed gift was removed. The picker renders again either way,
     * so the customer can claim it back — this only leaves a trace that they said no.
     */
    async checkForRemoval() {
      const claimedId = this.dataset.claimedVariantId;
      if (!claimedId) return;

      try {
        const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
        if (!response || !response.ok) return;

        const cart = await response.json();
        const stillPresent = cart.items?.some((item) => String(item.variant_id) === claimedId);
        if (stillPresent) return;

        this.rememberRejection(claimedId);

        // Clear the pointer so a later re-add of the same variant is not mistaken
        // for the original claim.
        await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes: { _gwp_claim: '' } }),
        });
      } catch (error) {
        // Never let this bookkeeping break the cart.
      }
    }

    rememberRejection(variantId) {
      try {
        window.sessionStorage.setItem(REJECTED_STORAGE_KEY, String(variantId));
      } catch (error) {
        // Private browsing and blocked storage are fine — this is a record, not a gate.
      }
    }

    /** Whether this visitor has already turned the gift down this session. */
    static hasRejected() {
      try {
        return window.sessionStorage.getItem(REJECTED_STORAGE_KEY) !== null;
      } catch (error) {
        return false;
      }
    }

    /* UI ---------------------------------------------------------------------- */

    setError(show) {
      if (this.errorEl) this.errorEl.hidden = !show;
    }
  }

  customElements.define('gift-claim', GiftClaim);
}

/*
 * PDP kit gift preview.
 *
 * The same product a gift slot (custom.gwp_gift, custom.gwp_gift_2) points a paid
 * case at (see gift-claim.liquid) can also be sold on that case's own page as a
 * paid "Make it a kit" add-on (see product-kit.liquid). When that slot's code is
 * already on the cart - the customer followed the Klaviyo link before reaching this
 * page - that add-on card should read as free instead of selling what checkout
 * will zero out anyway.
 *
 * Runs independently of the GiftClaim element above rather than through it: an
 * empty cart never renders <gift-claim> at all (see gift-claim.liquid), but a
 * customer can land straight on this page off the Klaviyo link with nothing in
 * their cart yet, so the preview has to work with no claim module in the DOM.
 * It shares gwpDiscountCodes with that module, so the two still cost one
 * /cart.js request between them when both happen to be on the page.
 *
 * product-kit.js itself carries no GWP knowledge: product-kit.liquid renders the
 * data-gwp-* attributes read below as plain data, and product-kit.js only ever
 * dispatches the generic product-kit:change event this listens for to redo the
 * override after the kit's own manual variant picker changes the card.
 */

/*
 * This is a preview, not a live price: a discount code does not reprice a cart
 * line the way an automatic discount does (see gift-line-status.liquid) - the
 * "Free" label only mirrors what checkout will do once the code applies.
 *
 * card.dataset.price is zeroed too, not just the visible text: ProductKit.total
 * (product-kit.js) reads that dataset value, uncontested, to add this card's price
 * into the main buy button's total (see VariantSelects.updatePrice in global.js) -
 * leaving it at the real price would tick the box for free while still charging
 * for it the moment the box is checked.
 *
 * The "was" figure struck through next to Free is the card's compare-at price when
 * it has one, otherwise its own price. It goes into card.dataset.comparePrice as
 * well, so ProductKit.compareTotal keeps counting the gift's worth once its price
 * is zero.
 *
 * Safe to run on every product-kit:change: a card still at '0' has already been
 * converted and is left alone. ProductKit.renderCard (the kit's own variant
 * picker) writes the real price back, which is what makes this run again.
 */
function applyKitGiftPreview(kit, card) {
  const currentPrice = Number(card.dataset.price) || 0;
  if (currentPrice === 0) return;

  const price = card.querySelector('[data-kit-price-current]');
  const compare = card.querySelector('[data-kit-price-compare]');
  const comparePrice = Number(card.dataset.comparePrice) || 0;

  if (compare) {
    if (comparePrice <= currentPrice) compare.innerHTML = price ? price.innerHTML : '';
    compare.hidden = !compare.innerHTML.trim();
  }
  card.dataset.comparePrice = String(Math.max(comparePrice, currentPrice));
  card.dataset.price = '0';

  if (price) price.textContent = kit.dataset.gwpFreeLabel || '';

  const badge = card.querySelector('[data-kit-badge]');
  if (badge) {
    badge.textContent = kit.dataset.gwpBadgeLabel || '';
    badge.hidden = !badge.textContent;
  }

  /*
   * Whatever last called ProductKit.updateTotals() (a colour change, a checkbox
   * toggle, the gift ticked in the other kit and mirrored here) already read this
   * card's price before the fix above landed - the await on gwpDiscountCodes in
   * previewKitGift means this always runs after that, never before it. Have the
   * kit redo its totals now that the card's price is actually right: that redraws
   * the buy box price for the main kit and fires product-kit:change, which the
   * standalone section's ProductAddToCart.render listens for. The nested
   * product-kit:change lands back here and returns at once, the price being 0.
   */
  kit.updateTotals?.();
}

/*
 * Gift product ids the customer has unticked on this page. A flag on the kit
 * element can't hold that: the buy box's kit is rebuilt from fresh Liquid on every
 * colour change (see the variantChange re-scan below), and the new kit would tick
 * the gift straight back - then mirror it into the standalone section's kit too.
 *
 * Only a real click lands here: ProductKit.hideCard and applySelection
 * (product-kit.js) set .checked directly, which fires no change event, and so
 * does tickKitGift. One listener on document, since the kits themselves get
 * replaced.
 */
const declinedKitGifts = new Set();

/*
 * The kit's gift slots in priority order - "productId:CODE,productId:CODE", as
 * product-kit.liquid renders them from the same slots gift-claim.liquid reads.
 */
function kitGiftSlots(kit) {
  return (kit.dataset.gwpGifts || '')
    .split(',')
    .map((pair) => pair.split(':').map((part) => part.trim()))
    .filter(([productId, code]) => productId && code)
    .map(([productId, code]) => ({ productId, code }));
}

document.addEventListener('change', (event) => {
  const checkbox = event.target;
  if (!checkbox.matches?.('[data-kit-checkbox]')) return;

  const card = checkbox.closest('[data-kit-card]');
  const kit = card?.closest('product-kit[data-gwp-gifts]');
  if (!kit || !kitGiftSlots(kit).some((slot) => slot.productId === card.dataset.productId)) return;

  if (checkbox.checked) declinedKitGifts.delete(card.dataset.productId);
  else declinedKitGifts.add(card.dataset.productId);
});

/*
 * The gift arrives ticked, once. This is not part of applyKitGiftPreview because
 * that re-runs on every product-kit:change - including the one an untick fires -
 * and would tick the box straight back. Goes through ProductKit.onCardChange so
 * the selected class, the other kit on the page and the totals all follow, the
 * same as a real click. A hidden card (the gift doesn't fit the current colour)
 * stays unticked, or it would go into the cart unseen, and so does one the
 * customer has already unticked.
 */
function tickKitGift(kit, card) {
  const checkbox = card.querySelector('[data-kit-checkbox]');
  if (!checkbox || checkbox.checked || card.hidden) return;
  if (declinedKitGifts.has(card.dataset.productId)) return;

  checkbox.checked = true;
  kit.onCardChange?.(card);
}

async function previewKitGift(kit) {
  /*
   * variantChange re-scans the whole page, but only the buy box's kit is replaced
   * with a fresh element - the standalone section's kit survives it. Without this
   * it would collect one more product-kit:change listener per colour change.
   */
  if (kit.gwpPreviewStarted) return;
  kit.gwpPreviewStarted = true;

  const cards = Array.from(kit.querySelectorAll('[data-kit-card]'));
  const slots = kitGiftSlots(kit)
    .map((slot) => ({ ...slot, card: cards.find((entry) => entry.dataset.productId === slot.productId) }))
    .filter((slot) => slot.card);
  if (!slots.length) return;

  /*
   * At most one card turns free: the first slot whose code is on the cart, the
   * same "slot 1 wins" the claim module enforces through data-higher-priority-codes.
   * The codes don't combine at checkout, so freeing a second card would promise a
   * gift checkout won't give. A slot whose gift isn't in this kit doesn't outrank
   * anything, as in gift-claim.liquid, where only rendered slots count.
   *
   * Nor does a slot whose gift product is already in the cart - claimed there, or
   * added paid from this or another page. The discount zeroes one qualifying line, so
   * that line is the gift, and a second one from this kit would be charged at
   * checkout despite reading Free here. Same presence rule as gift-claim.liquid, which
   * is also what stops the cart offering a second gift once this kit's is added: the
   * kit adds it as a plain line, with no _gwp_claim to recognise it by.
   */
  const [onCart, inCart] = await Promise.all([
    Promise.all(slots.map((slot) => gwpDiscountCodes.has(slot.code))),
    Promise.all(slots.map((slot) => gwpDiscountCodes.hasProduct(slot.productId))),
  ]);
  const gift = slots.find((_, index) => onCart[index] && !inCart[index]);
  if (!gift) return;

  const { card } = gift;
  applyKitGiftPreview(kit, card);
  tickKitGift(kit, card);
  kit.addEventListener('product-kit:change', () => applyKitGiftPreview(kit, card));
}

function scanForKitGiftPreviews() {
  document.querySelectorAll('product-kit[data-gwp-gifts]').forEach(previewKitGift);
}

scanForKitGiftPreviews();

/*
 * The main product's own variant picker replaces this whole block with fresh,
 * cart-blind Liquid HTML on every change - see VariantSelects.renderProductInfo in
 * global.js, which re-renders #ComplementaryKit-* wholesale into a brand new
 * <product-kit> with none of the listeners above. Re-scan whenever that fires.
 * The kit's own manual variant picker doesn't go through that path - that's what
 * the product-kit:change listener in previewKitGift is for.
 */
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, scanForKitGiftPreviews);
}
